import { FC, useEffect, useState } from "react"
import { observer } from "mobx-react"
import { useStore } from "stores/utils/use-store"
import { Button, Card, Space, Switch, Table, Tag, Tooltip, Typography } from "antd"
import { notifications } from "notifications"
import { showDeleteConfirm } from "components"
import { AbExperiment } from "../store/types"
import { ForceAccountsModal } from "./force-accounts-modal"
import { SetExperimentModal } from "./set-experiment-modal"

export const AbTestingPage: FC = observer(() => {
  const { dataStore: { abTestingStore } } = useStore()
  const [showModal, setShowModal] = useState(false)
  const [prefillExperiment, setPrefillExperiment] = useState<AbExperiment | null>(null)
  const [debugExperiment, setDebugExperiment] = useState<AbExperiment | null>(null)

  useEffect(() => {
    abTestingStore.loadExperiments()
  }, [abTestingStore])

  const handleSet = async(data: Parameters<typeof abTestingStore.setExperiment>[0]) => {
    try {
      await abTestingStore.setExperiment(data)
      notifications.success("Experiment set successfully")
    } catch (err: any) {
      notifications.error(err?.cause?.message || err?.message || "Failed to set experiment")
      throw err
    }
  }

  const handleEnd = (experiment: AbExperiment) => {
    showDeleteConfirm(
      `experiment "${experiment.name}"`,
      "This will end the active experiment.",
      async() => {
        try {
          await abTestingStore.endExperiment(experiment.name)
          notifications.success(`Experiment "${experiment.name}" ended`)
        } catch (err: any) {
          notifications.error(err?.cause?.message || err?.message || "Failed to end experiment")
        }
      },
    )
  }

  const columns = [
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      render: (name: string) => <Typography.Text strong>{name}</Typography.Text>,
    },
    {
      title: "Status",
      key: "status",
      width: 100,
      render: (_: any, record: AbExperiment) => (
        <Tag color={record.endedAt ? "default" : "green"}>
          {record.endedAt ? "Ended" : "Active"}
        </Tag>
      ),
    },
    {
      title: "Started",
      dataIndex: "startedAt",
      key: "startedAt",
      width: 180,
      render: (v: string) => new Date(v).toLocaleString(),
    },
    {
      title: "Ended",
      dataIndex: "endedAt",
      key: "endedAt",
      width: 180,
      render: (v: string | null) => (v ? new Date(v).toLocaleString() : "—"),
    },
    {
      title: "Variants",
      key: "variants",
      render: (_: any, record: AbExperiment) => (
        <Space wrap>
          {record.variants.map(v => (
            <Tooltip
              key={v.name}
              title={
                <div>
                  <div>Cutoff: {(v.upperCutoffPercentage * 100).toFixed(0)}%</div>
                  {v.forceOnAccountIds.length > 0 && <div>Force ON: {v.forceOnAccountIds.length} accounts</div>}
                  {v.forceOffAccountIds.length > 0 && <div>Force OFF: {v.forceOffAccountIds.length} accounts</div>}
                  {!!v.payload && <div>Payload: {JSON.stringify(v.payload)}</div>}
                </div>
              }
            >
              <Tag color="blue">{v.name} ({(v.upperCutoffPercentage * 100).toFixed(0)}%)</Tag>
            </Tooltip>
          ))}
        </Space>
      ),
    },
    {
      title: "Actions",
      key: "actions",
      width: 180,
      render: (_: any, record: AbExperiment) => (
        <Space>
          <Button
            type="link"
            onClick={() => {
              setPrefillExperiment(record)
              setShowModal(true)
            }}
          >
            Re-set
          </Button>
          {!record.endedAt && (
            <>
              <Button type="link" onClick={() => setDebugExperiment(record)}>
                Debug
              </Button>
              <Button type="link" danger onClick={() => handleEnd(record)}>
                End
              </Button>
            </>
          )}
        </Space>
      ),
    },
  ]

  return (
    <div>
      <Space direction="vertical" size="large" style={{ width: "100%" }}>
        <Card
          title="AB Testing Experiments"
          extra={
            <Space>
              <Switch
                checked={!abTestingStore.includeEnded}
                onChange={v => abTestingStore.setIncludeEnded(!v)}
                checkedChildren="Active"
                unCheckedChildren="All"
              />
              <Button
                type="primary"
                onClick={() => {
                  setPrefillExperiment(null)
                  setShowModal(true)
                }}
              >
                Set Experiment
              </Button>
            </Space>
          }
        >
          <Table
            dataSource={abTestingStore.experiments}
            columns={columns}
            rowKey="id"
            loading={abTestingStore.isLoading}
            pagination={false}
          />
        </Card>
      </Space>

      <SetExperimentModal
        visible={showModal}
        onClose={() => {
          setShowModal(false)
          setPrefillExperiment(null)
        }}
        onSubmit={handleSet}
        initialExperiment={prefillExperiment}
      />

      <ForceAccountsModal
        visible={!!debugExperiment}
        experiment={debugExperiment}
        onClose={() => setDebugExperiment(null)}
        onSubmit={async data => {
          await abTestingStore.updateVariantsForceAccounts(debugExperiment!.name, data)
        }}
      />
    </div>
  )
})
